import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

/**
 * Les quatre types d'exercice interactifs rendus dans une bulle de conversation
 * (à ne pas confondre avec les huit types d'items du domaine Practice, qui
 * vivent dans `features/practice/types.ts` et n'ont aucun rapport).
 */
export type ExerciseKind = 'mcq' | 'open' | 'code' | 'fill'

/**
 * Résultat de correction d'un exercice, calculé côté client, prêt à entrer
 * dans le message groupé envoyé à l'IA (voir DEFAUTS-EXERCICES.md, "Contrat
 * de soumission"). `correct` vaut `null` pour les types non auto-corrigeables
 * (`open`, `code`) : ils comptent hors score, l'IA seule les commente.
 */
export interface ExerciseGrade {
  itemType: ExerciseKind
  question: string
  /** Résumé lisible de la réponse donnée, déjà formaté pour le message groupé. */
  answerLabel: string
  correct: boolean | null
  /**
   * Vrai si l'élève a réellement saisi une réponse (même prédicat que le
   * `hasAnswer` transmis à `useExerciseSlot`). Un exercice auto-corrigeable
   * laissé vide via « valider quand même » a `correct: false` par construction
   * (aucune option sélectionnée n'est jamais la bonne) : sans ce champ,
   * `ExerciseSetProvider` ne peut pas distinguer une réponse fausse d'une
   * réponse absente, et compterait l'omission comme une erreur dans le score
   * et dans `RecordQuizAnswerHandler`.
   */
  answered: boolean
}

interface ExerciseSlotState {
  hasAnswer: boolean
  grade: () => ExerciseGrade
}

export interface ExerciseSetContextValue {
  /** Vrai une fois la série validée : verrouille tous les exercices de la série. */
  locked: boolean
  registerSlot: (index: number, state: ExerciseSlotState) => void
  unregisterSlot: (index: number) => void
}

/**
 * Contexte collectant tous les blocs ```exercise d'un même message assistant
 * (voir DEFAUTS-EXERCICES.md, D1). Monté une fois par message par
 * `ExerciseSetProvider`, jamais utilisé directement en dehors de ce fichier
 * et de `useExerciseSlot`.
 */
export const ExerciseSetContext = createContext<ExerciseSetContextValue | null>(null)

/**
 * Hook d'inscription utilisé par chaque composant d'exercice (mcq, open,
 * code, fill). Le composant se déclare au montage à sa position dans le
 * message (`index`, dans l'ordre d'apparition), met à jour son état de
 * réponse à chaque frappe SANS RIEN ENVOYER, et lit en retour si la série a
 * été verrouillée par la validation groupée.
 *
 * Hors de tout `ExerciseSetProvider` (tests isolés d'un composant, par
 * exemple), le contexte est absent : l'exercice reste alors simplement
 * jamais verrouillé, ce qui permet de continuer à monter chaque composant
 * seul dans ses propres tests.
 */
export function useExerciseSlot(
  index: number,
  hasAnswer: boolean,
  grade: () => ExerciseGrade,
): { locked: boolean } {
  const ctx = useContext(ExerciseSetContext)
  const gradeRef = useRef(grade)

  // Garde la dernière closure de correction à jour sans re-déclencher
  // l'inscription ci-dessous à chaque frappe : l'écriture se fait après le
  // rendu (effet sans dépendances), jamais pendant le rendu lui-même.
  useEffect(() => {
    gradeRef.current = grade
  })

  useEffect(() => {
    if (!ctx) return
    ctx.registerSlot(index, { hasAnswer, grade: () => gradeRef.current() })
    return () => ctx.unregisterSlot(index)
  }, [ctx, index, hasAnswer])

  return { locked: ctx?.locked ?? false }
}

/**
 * Hook interne à `ExerciseSetProvider` : tient le registre des exercices
 * inscrits dans une ref (pas de re-rendu à chaque frappe) et ne recalcule les
 * compteurs, via `useState`, que lorsque le nombre d'exercices ou leur état
 * "répondu" change — c'est tout ce dont la barre de validation a besoin pour
 * s'activer. Les compteurs sont de l'état, jamais lus depuis la ref pendant
 * le rendu : seuls `registerSlot`/`unregisterSlot`, appelés depuis les effets
 * d'inscription des exercices, la mutent.
 */
export function useExerciseSlotRegistry() {
  const registryRef = useRef<Map<number, ExerciseSlotState>>(new Map())
  const [counts, setCounts] = useState({ total: 0, answered: 0 })

  const recomputeCounts = useCallback(() => {
    const slots = [...registryRef.current.values()]
    setCounts({ total: slots.length, answered: slots.filter((s) => s.hasAnswer).length })
  }, [])

  const registerSlot = useCallback(
    (index: number, state: ExerciseSlotState) => {
      const previous = registryRef.current.get(index)
      registryRef.current.set(index, state)
      if (previous?.hasAnswer !== state.hasAnswer) {
        recomputeCounts()
      }
    },
    [recomputeCounts],
  )

  const unregisterSlot = useCallback(
    (index: number) => {
      if (registryRef.current.delete(index)) recomputeCounts()
    },
    [recomputeCounts],
  )

  const getSortedGrades = useCallback((): ExerciseGrade[] => {
    return [...registryRef.current.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, slot]) => slot.grade())
  }, [])

  return { registerSlot, unregisterSlot, getSortedGrades, total: counts.total, answered: counts.answered }
}
